<template>
    <div class="py-4" :class="{'px-4': !isMobile}">
        <breadcrumbs v-if="!hideBreadcrumbs" />
        <div v-if="step === 1">
            <v-tabs v-if="!productType" v-model="tab" :show-arrows="!isMobile" center-active class="mb-8">
                <v-tab v-for="tab in tabs" :key="tab.name" :value="tab.type">
                    <v-icon class="me-1">
                        {{ tab.ico }}
                    </v-icon>
                    {{ $t(tab.title) }}
                </v-tab>
            </v-tabs>
            <div class="mb-4 text-body-2 text-primary cursor-pointer" @click="expanded = !expanded">
                {{ $t('filters') }}
                <v-icon color="primary" size="small">
                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
            </div>
            <v-expand-transition>
                <v-row v-show="expanded" class="mb-4">
                    <v-col cols="12" md="4">
                        <order-service-daterange
                            :start-date-label="$t('create_date_from')"
                            :end-date-label="$t('create_date_to')"
                            :outlined="false"
                            :single-line="false"
                            :no-title="true"
                            dense
                            :_start-date="startDate"
                            :_end-date="endDate"
                            @change-start-date="startDate = $event"
                            @change-end-date="endDate = $event"
                        />
                    </v-col>
                    <v-col v-if="!productId" cols="12" md="2">
                        <country-autocomplete v-model="country" :label="$t('country')" dense @change="city = null" />
                    </v-col>
                    <v-col v-if="!productId" cols="12" md="2">
                        <city-autocomplete v-model="city" :label="$t('city')" dense @change="country = null" />
                    </v-col>
                    <v-col cols="2">
                        <v-btn color="primary" :disabled="isLoading" @click="load">{{ $t('load') }}</v-btn>
                    </v-col>
                </v-row>
            </v-expand-transition>
            <span v-if="startDate && endDate" class="text-caption mb-4">
                {{ `${$filters.dateShortFormat(startDate)} - ${$filters.dateShortFormat(endDate)}` }}
            </span>
            <v-tabs-window v-model="tab">
                <v-tabs-window-item value="EXCURSION">
                    <sales-monitoring-report-table
                        v-if="statistics.excursionStatistics"
                        :statistics-data="statistics.excursionStatistics"
                        :is-loading="isLoading"
                        type="EXCURSION"
                        @show-details="nextStep"
                    />
                </v-tabs-window-item>
                <v-tabs-window-item value="TRANSFER">
                    <sales-monitoring-report-table
                        v-if="statistics.transferStatistics"
                        :statistics-data="statistics.transferStatistics"
                        :is-loading="isLoading"
                        type="TRANSFER"
                        @show-details="nextStep"
                    />
                </v-tabs-window-item>
                <v-tabs-window-item value="FLIGHT">
                    <sales-monitoring-report-table
                        v-if="statistics.flightStatistics"
                        :statistics-data="statistics.flightStatistics"
                        :is-loading="isLoading"
                        type="FLIGHT"
                        @show-details="nextStep"
                    />
                </v-tabs-window-item>
            </v-tabs-window>
        </div>

        <div v-if="step === 2">
            <v-btn color="primary" variant="text" class="mb-4" @click="nextStep">{{ $t('back') }}</v-btn>
            <detailed-sales-monitoring-report
                v-if="selectedProduct"
                :selected-product="selectedProduct"
                :selected-date="selectedDate"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import {AuthInfoMixin} from '~src/mixins/authInfoMixin.src'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import SalesMonitoringReportTable from '@/components/account/SalesMonitoringReportTable.vue'
    import DetailedSalesMonitoringReport from '@/components/account/DetailedSalesMonitoringReport.vue'
    import CountryAutocomplete from '~/components/search/forms/CountryAutocomplete.vue'

    @Component({
        components: {
            Breadcrumbs,
            OrderServiceDaterange,
            CityAutocomplete,
            SalesMonitoringReportTable,
            DetailedSalesMonitoringReport,
            CountryAutocomplete,
        },
    })
    export class BusinessIntelligencePage extends mixins(AuthInfoMixin) {
        @Prop({default: false, type: Boolean}) hideBreadcrumbs
        @Prop() productType
        @Prop() productId

        expanded = true
        isLoading = false
        tab = null
        step = 1
        startDate = null
        endDate = null
        city = null
        country = null
        statistics = {}
        selectedProduct = null
        selectedDate = null

        async mounted() {
            if (this.productType) {
                this.tab = this.tabs.find(t => t.type === this.productType)?.type
            } else this.tab = this.tabs[0].type

            this.startDate = new Date()
            this.endDate = this.$dateFns.addDays(new Date(), 7)
        }

        async load() {
            this.isLoading = true
            const request = {
                startDate: this.$dateFns.format(this.startDate),
                endDate: this.$dateFns.format(this.endDate),
                productType: this.selectedTab.type,
                ...(this.city?.id && {cityId: this.city.id}),
                ...(this.country?.id && {countryId: this.country.id}),
                ...(this.productId && {productId: this.productId}),
            }
            try {
                if (this.selectedTab.type === 'EXCURSION') this.statistics.excursionStatistics = []
                else if (this.selectedTab.type === 'TRANSFER') this.statistics.transferStatistics = []
                else if (this.selectedTab.type === 'FLIGHT') this.statistics.flightStatistics = []
                const {productStatistics} = await this.$api.salesMonitoringReport.get(request)
                if (this.selectedTab.type === 'EXCURSION') this.statistics.excursionStatistics = productStatistics
                else if (this.selectedTab.type === 'TRANSFER') this.statistics.transferStatistics = productStatistics
                else if (this.selectedTab.type === 'FLIGHT') this.statistics.flightStatistics = productStatistics
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoading = false
            }
        }

        nextStep({product, dayIndex}) {
            this.selectedProduct = product ? product : null
            this.selectedDate =
                product?.itemsByDate?.length && product.itemsByDate[dayIndex]
                    ? this.$dateFns.format(product.itemsByDate[dayIndex].date)
                    : null

            this.step = this.step === 1 ? 2 : 1
        }

        get tabs() {
            const tabs = []

            if (this.activeProducts.includes('activities')) {
                tabs.push({
                    ico: 'mdi-lightning-bolt-outline',
                    title: 'activities.activities',
                    type: 'EXCURSION',
                })
            }
            if (this.activeProducts.includes('transfers')) {
                tabs.push({
                    ico: 'mdi-bus',
                    title: 'transfers.transfers',
                    type: 'TRANSFER',
                })
            }
            if (this.activeProducts.includes('flights')) {
                tabs.push({
                    ico: 'mdi-airplane',
                    title: 'flights.flights',
                    type: 'FLIGHT',
                })
            }

            return tabs
        }

        get selectedTab() {
            return this.tabs.find(({type}) => type === this.tab)
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default BusinessIntelligencePage
</script>
